import React, { useState } from 'react';
import { Layers, Loader2, CheckCircle2, XCircle, FileText, FileDown, Clock, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { receiptUrl } from './StanbicCipsPanel';
import { downloadPdf } from '../lib/download';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (v) => `¥${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function BatchReviewCard({ batch, token, onUpdated, onSelectOrder }) {
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [autoProcure, setAutoProcure] = useState(true);
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const isImage = batch.receipt?.content_type?.startsWith('image/');
  const has1688 = batch.orders.some((o) => /1688\.com\/offer\/\d+/.test(o.product_url || ''));

  const act = async (kind) => {
    setBusy(kind);
    const waWin = window.open('', '_blank');
    try {
      const body = kind === 'confirm' ? { note, auto_procure: autoProcure && has1688 } : { note };
      const res = await fetch(`${API}/api/payments/admin/batches/${batch.id}/${kind}`, { method: 'POST', headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Action failed');
      if (kind === 'confirm') {
        toast.success(`Combined payment ${batch.reference} verified — ${batch.orders.length} orders moved to Domestic Transit`);
        (data.results || []).forEach((r) => {
          if (r.procurement) toast.success(`#${r.order_id}: 1688 order ${r.procurement.alibaba_order_id} created`);
          else if (r.procurement_error) toast.warning(`#${r.order_id}: 1688 auto-order skipped — ${r.procurement_error}`);
        });
      } else toast.success('Receipt rejected — client notified');
      setNote(''); setRejecting(false);
      onUpdated();
      if (waWin) waWin.location.href = data.whatsapp_url;
    } catch (err) { if (waWin) waWin.close(); toast.error(err.message); }
    finally { setBusy(''); }
  };

  return (
    <div className="rounded-xl border-2 border-violet-300 bg-violet-50 p-4" data-testid={`batch-review-${batch.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-bold text-sm text-gray-900 flex items-center gap-2"><Layers className="w-4 h-4 text-violet-700" /> Combined payment <span className="font-mono text-violet-800">{batch.reference}</span></span>
        <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded border bg-amber-100 text-amber-800 border-amber-300 flex items-center gap-1"><Clock className="w-3 h-3" /> Awaiting verification</span>
        <span className="text-xs text-gray-500">{batch.client_name} ({batch.client_whatsapp})</span>
        <strong className="ml-auto text-emerald-700" data-testid={`batch-total-${batch.id}`}>{money(batch.total_rmb)}</strong>
      </div>

      <div className="mt-3 flex flex-col sm:flex-row gap-3">
        {batch.receipt && (
          <a href={receiptUrl(batch.receipt)} target="_blank" rel="noopener noreferrer" className="flex-shrink-0" data-testid={`batch-receipt-link-${batch.id}`}>
            {isImage ? <img src={receiptUrl(batch.receipt)} alt="Receipt" className="w-28 h-28 object-cover rounded-lg border bg-white" /> : (
              <div className="w-28 h-28 rounded-lg border bg-white flex flex-col items-center justify-center text-blue-700 gap-1"><FileText className="w-7 h-7" /><span className="text-xs font-semibold">Open PDF</span></div>
            )}
          </a>
        )}
        <div className="flex-1 text-xs text-gray-700 space-y-1">
          <p><span className="text-gray-400">Uploaded:</span> {batch.submitted_at ? new Date(batch.submitted_at).toLocaleString() : '—'} · {batch.receipt?.filename}</p>
          <ul className="divide-y divide-violet-100 border border-violet-200 rounded-lg bg-white" data-testid={`batch-orders-${batch.id}`}>
            {batch.orders.map((o) => (
              <li key={o.id} className="flex items-center gap-2 px-2 py-1.5">
                <button type="button" onClick={() => onSelectOrder?.(o.id)} className="font-mono text-blue-600 hover:underline" data-testid={`batch-order-link-${batch.id}-${o.id}`}>#{o.id}</button>
                <span className="truncate flex-1">{o.product_title}</span>
                <span className="text-gray-400 whitespace-nowrap">×{o.quantity}</span>
                <span className="font-semibold whitespace-nowrap">{money(o.total_rmb)}</span>
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => downloadPdf(`${API}/api/payments/admin/batches/${batch.id}/proforma`, { Authorization: `Bearer ${token}` }, `Proforma-${batch.reference}.pdf`)}
            className="inline-flex items-center gap-1 text-blue-600 hover:underline font-medium" data-testid={`batch-proforma-${batch.id}`}><FileDown className="w-3.5 h-3.5" /> Proforma invoice PDF</button>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-violet-200 space-y-2">
        {has1688 && (
          <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
            <input type="checkbox" checked={autoProcure} onChange={(e) => setAutoProcure(e.target.checked)} className="accent-orange-600" data-testid={`batch-auto-procure-${batch.id}`} />
            <ShoppingBag className="w-3.5 h-3.5 text-orange-600" /> Auto-place 1688 purchase orders on approval (default receiving address)
          </label>
        )}
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={rejecting ? 'Reason for rejection (sent to client)…' : 'Verification note (optional)…'}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:border-orange-500 focus:outline-none" data-testid={`batch-note-${batch.id}`} />
        <div className="flex flex-col sm:flex-row gap-2">
          {!rejecting ? (
            <>
              <Button onClick={() => act('confirm')} disabled={!!busy} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold" data-testid={`batch-approve-btn-${batch.id}`}>
                {busy === 'confirm' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Approve CIPS RMB — all {batch.orders.length} orders → Domestic Transit
              </Button>
              <Button variant="outline" onClick={() => setRejecting(true)} disabled={!!busy} className="border-red-300 text-red-700 hover:bg-red-50 text-xs" data-testid={`batch-reject-btn-${batch.id}`}><XCircle className="w-4 h-4 mr-1" /> Reject receipt</Button>
            </>
          ) : (
            <>
              <Button onClick={() => act('reject')} disabled={!!busy || !note.trim()} className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold" data-testid={`batch-reject-submit-${batch.id}`}>
                {busy === 'reject' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />} Send rejection to client
              </Button>
              <Button variant="outline" onClick={() => { setRejecting(false); setNote(''); }} className="text-xs" data-testid={`batch-reject-cancel-${batch.id}`}>Back</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
